import type { PropsWithChildren } from "react"
import { startOfDay } from "date-fns"
import React, {
    useState,
    useMemo,
    createContext,
    useContext,
    useEffect,
} from "react"

import type {
    ListingFieldsFragment,
    PricingPackageFieldsFragment,
    QuoteFieldsFragment,
    WidgetFieldsFragment,
} from "~graphql/generated/graphql"
import useCheckoutStepByStepFlowState from "~utils/use-checkout-step-by-step-flow-state"
import {
    getDefaultPackageState,
    getEditableFields,
    getInitialCheckoutFlowState,
    getQuotePackage,
    getStepConfigs,
    mapDatesToTimestamps,
} from "~components/pages/listing/checkout/checkout-flow/checkout-flow-helpers"
import type {
    CheckoutFlowContextValue,
    PackageState,
    BookingInvitePackageInfo,
} from "~components/pages/listing/checkout/checkout-flow/checkout-flow-types"
import { useListingAvailability } from "~utils/hooks/use-availability"
import { CHECKOUT_STEPS } from "~config/constants"
import { navigateToListing } from "~utils/navigation-helpers"
import { getFirstAvailableDate } from "~utils/helpers"
import type {
    CheckoutQueryParams,
    UpdateCheckoutQueryParamsFunc,
} from "~utils/use-checkout-query-params"
import { DEFAULT_CHECKOUT_QUERY_PARAMS } from "~utils/use-checkout-query-params"
import usePricingPackage from "~utils/use-pricing-package"

interface Props extends PropsWithChildren {
    readonly listing: ListingFieldsFragment
    readonly bookingInvitePackageInfo?: BookingInvitePackageInfo | null
    readonly pricingPackage?: PricingPackageFieldsFragment | null
    readonly quote?: QuoteFieldsFragment
    readonly checkoutQueryParams: CheckoutQueryParams
    readonly updateCheckoutQueryParams: UpdateCheckoutQueryParamsFunc
    readonly widget?: WidgetFieldsFragment | null
    readonly isWidget?: boolean
}

const CheckoutFlowContext = createContext<CheckoutFlowContextValue>({
    dates: [],
    setDates: () => {},
    currentMonth: new Date(),
    setCurrentMonth: () => {},
    packageState: getDefaultPackageState(),
    updatePackageState: () => {},
    currentStep: null,
    goToStep: () => {},
    goToNextStep: () => {},
    goToPreviousStep: () => {},
    goToFirstStep: () => {},
    hasPreviousStep: false,
    hasNextStep: false,
    bookingInvitePackageInfo: null,
    listing: null,
    editableFields: getEditableFields(),
    stepConfigs: [],
    quote: null,
    pricingPackage: null,
    checkoutQueryParams: DEFAULT_CHECKOUT_QUERY_PARAMS,
    updateCheckoutQueryParams: () => {},
    quotePackageBeingEdited: null,
    availableDates: [],
    isLoadingAvailability: false,
    handleOnBackClick: () => {},
    widget: null,
    isWidget: false,
})

export default function CheckoutFlowProvider({
    listing,
    bookingInvitePackageInfo,
    pricingPackage: initialPricingPackage,
    quote,
    checkoutQueryParams,
    updateCheckoutQueryParams,
    widget,
    isWidget,
    children,
}: Props) {
    const quotePackageBeingEdited = getQuotePackage(quote)
    const { initialDates, initialPackageState } = getInitialCheckoutFlowState({
        pricingPackage: initialPricingPackage,
        bookingInvitePackageInfo,
        quotePackageBeingEdited,
        quote,
        persistedCheckoutState: checkoutQueryParams.checkout_state,
    })

    const initialCurrentMonth = getFirstAvailableDate(listing)

    const [dates, setDates] = useState<Date[]>(initialDates)
    // used for the date picker
    const [currentMonth, setCurrentMonth] = useState<Date>(initialCurrentMonth)

    const [packageState, setPackageState] =
        useState<PackageState>(initialPackageState)

    // When a quote loads, update the dates. This is important for the widget when resuming a widget session.
    // When resuming a widget session, the initial state has already been set and won't use the newly loaded quote.
    // Once a quote has been created, the dates are no longer editable, so we don't need to worry about overwriting selected dates.
    useEffect(() => {
        if (!quote) return

        setDates(initialDates)
    }, [quote])

    const editableFields = getEditableFields(bookingInvitePackageInfo)
    const stepConfigs = getStepConfigs({
        quote,
        pricingPackage: initialPricingPackage,
        bookingInvitePackageInfo,
        quotePackageBeingEdited,
        isWidget,
    })

    const { availableDates, isLoading: isLoadingAvailability } =
        useListingAvailability({
            listingId: listing.id,
            referenceDate: startOfDay(currentMonth).getTime(),
            numberOfGuests: packageState.number_of_guests,
        })
    const { pricingPackage } = usePricingPackage(
        packageState.pricing_package_id
    )

    const {
        currentStep,
        goToStep,
        goToNextStep,
        goToPreviousStep,
        goToFirstStep,
        hasPreviousStep,
        hasNextStep,
    } = useCheckoutStepByStepFlowState({
        stepConfigs,
        checkoutState: {
            dates: mapDatesToTimestamps(dates),
            packageState,
        },
        updateCheckoutQueryParams,
    })

    const updatePackageState = (update: Partial<PackageState>) => {
        setPackageState((curState) => ({ ...curState, ...update }))
    }

    const handleOnBackClick = () => {
        // If there's a previous step, go to it.
        if (hasPreviousStep) return goToPreviousStep()

        // If we don't have a quote yet, go to the listing page.
        if (!quote) return navigateToListing(listing.id)

        // If there's no previous step, and we have a quote, it means we
        // are editing or adding a package. In this case, go back to summary.
        return goToStep({
            step: CHECKOUT_STEPS.SUMMARY,
            package_id: null,
            quote_package_id: null,
            checkout_state: null,
        })
    }

    const contextValue = useMemo(
        () => ({
            dates,
            setDates,
            currentMonth,
            setCurrentMonth,
            packageState,
            updatePackageState,
            currentStep,
            goToStep,
            goToPreviousStep,
            goToNextStep,
            hasPreviousStep,
            hasNextStep,
            goToFirstStep,
            bookingInvitePackageInfo,
            listing,
            editableFields,
            stepConfigs,
            quote,
            pricingPackage,
            checkoutQueryParams,
            updateCheckoutQueryParams,
            quotePackageBeingEdited,
            availableDates,
            isLoadingAvailability,
            handleOnBackClick,
            widget,
            isWidget,
        }),
        [
            currentMonth,
            packageState,
            currentStep,
            bookingInvitePackageInfo,
            dates,
            quote,
            pricingPackage,
            checkoutQueryParams,
            quotePackageBeingEdited,
            availableDates,
            isLoadingAvailability,
            handleOnBackClick,
            widget,
            isWidget,
        ]
    )

    return (
        <CheckoutFlowContext.Provider value={contextValue}>
            {children}
        </CheckoutFlowContext.Provider>
    )
}

export function useCheckoutFlow() {
    return useContext(CheckoutFlowContext)
}
